import React from 'react';
import {
    Sun,
    Moon,
    Sparkles,
    MessageSquare,
    FolderOpen,
    Shield,
    Settings,
    Monitor,
    LogOut,
} from 'lucide-react';

const TeacherWorkspaceHeader = ({
    selectedSubject,
    gradeLevel,
    lastGeneratedResult,
    subjects,
    onSelectSubject,
    isDark,
    onToggleTheme,
    showAssistantDrawer,
    onOpenAssistant,
    activePipeline,
    onToggleFeedback,
    onOpenSavedLessons,
    isAdmin,
    hasDevCenterAccess,
    onEnterAdminWorkspace,
    onEnterDevCenter,
    showAdminMenu,
    onToggleAdminMenu,
    onCloseAdminMenu,
    onOpenAccountSettings,
    onSwitchToAssistant,
    onShowLogoutConfirm,
    onReturnDashboard,
}) => {
    return (
        <div className="bg-white/80 backdrop-blur-md border-b border-[#f5f5f7] sticky top-0 z-40">
            <div className="max-w-[960px] mx-auto px-6 h-14 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={onReturnDashboard}
                        className="font-semibold tracking-tight text-base text-[#1d1d1f] hover:opacity-70 transition-opacity flex items-center gap-1.5 cursor-pointer"
                        title="Return to Dashboard"
                    >
                        <span className="text-[#FFC107]">Saige</span>
                    </button>
                    <span className="text-xs text-[#999] font-medium hidden sm:inline">
                        {selectedSubject} · Grade {gradeLevel}
                    </span>
                    {lastGeneratedResult?.data?.template_info && (
                        <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFC107]/10 text-[10px] font-medium text-[#b8860b]" title={`Source: ${lastGeneratedResult.data.template_info.template_source}`}>
                            {lastGeneratedResult.data.template_info.is_district_enforced && (
                                <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor"><path d="M8 1l2 3h3l-1 3 1 3h-3l-2 3-2-3H3l1-3-1-3h3z" /></svg>
                            )}
                            {lastGeneratedResult.data.template_info.template_name || 'Saige Default'}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1.5 md:gap-2">
                    {subjects.length > 1 && (
                        <select
                            value={selectedSubject}
                            onChange={(e) => onSelectSubject(e.target.value)}
                            className="px-3 py-1.5 rounded-xl border border-black/5 text-sm text-[#1d1d1f] focus:outline-none focus:border-[#FFC107] bg-transparent"
                        >
                            {subjects.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    )}

                    <button
                        onClick={onToggleTheme}
                        className="p-2 text-[#999] hover:text-[#FFC107] transition-colors"
                        title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    >
                        {isDark ? <Sun size={18} /> : <Moon size={18} />}
                    </button>

                    <button
                        onClick={onOpenAssistant}
                        className={`p-2 transition-colors ${showAssistantDrawer ? 'text-[#FFC107]' : 'text-[#999] hover:text-[#FFC107]'}`}
                        title="Saige Assistant"
                    >
                        <Sparkles size={18} />
                    </button>

                    <button
                        onClick={onToggleFeedback}
                        className={`p-2 transition-colors ${activePipeline === 'feedback' ? 'text-[#FFC107]' : 'text-[#999] hover:text-[#FFC107]'}`}
                        title="Send Feedback"
                    >
                        <MessageSquare size={18} />
                    </button>
                    <button
                        onClick={onOpenSavedLessons}
                        className="p-2 text-[#999] hover:text-[#FFC107] transition-colors"
                        title="My Lessons Library"
                    >
                        <FolderOpen size={18} />
                    </button>

                    {(isAdmin || hasDevCenterAccess) && (() => {
                        const showBoth = isAdmin && onEnterAdminWorkspace && hasDevCenterAccess && onEnterDevCenter;

                        if (showBoth) {
                            return (
                                <div className="relative" key="admin-menu">
                                    <button
                                        onClick={onToggleAdminMenu}
                                        className={`p-2 transition-colors ${showAdminMenu ? 'text-[#FFC107]' : 'text-[#999] hover:text-[#FFC107]'}`}
                                        title="Admin Menu"
                                    >
                                        <Shield size={18} />
                                    </button>
                                    {showAdminMenu && (
                                        <>
                                            <div className="fixed inset-0 z-40" onClick={onCloseAdminMenu} />
                                            <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-black/5 py-1.5 z-50">
                                                <button
                                                    onClick={() => { onCloseAdminMenu(); onEnterAdminWorkspace(); }}
                                                    className="w-full px-4 py-2.5 text-left text-sm text-[#1d1d1f] hover:bg-[#fafafa] flex items-center gap-3 transition-colors"
                                                >
                                                    <Shield size={16} className="text-[#999]" />
                                                    <div>
                                                        <div className="font-medium">Admin Workspace</div>
                                                        <div className="text-[11px] text-[#999] mt-0.5">Campus &amp; district management</div>
                                                    </div>
                                                </button>
                                                <div className="mx-3 border-t border-black/5" />
                                                <button
                                                    onClick={() => { onCloseAdminMenu(); onEnterDevCenter(); }}
                                                    className="w-full px-4 py-2.5 text-left text-sm text-[#1d1d1f] hover:bg-[#fafafa] flex items-center gap-3 transition-colors"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500">
                                                        <rect x="2" y="3" width="20" height="14" rx="2" />
                                                        <line x1="8" y1="21" x2="16" y2="21" />
                                                        <line x1="12" y1="17" x2="12" y2="21" />
                                                    </svg>
                                                    <div>
                                                        <div className="font-medium">Dev Command Center</div>
                                                        <div className="text-[11px] text-[#999] mt-0.5">Deployments &amp; development ops</div>
                                                    </div>
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        }

                        if (isAdmin && onEnterAdminWorkspace) {
                            return (
                                <button key="admin-only" onClick={onEnterAdminWorkspace} className="p-2 text-[#999] hover:text-[#FFC107] transition-colors" title="Admin Dashboard">
                                    <Shield size={18} />
                                </button>
                            );
                        }

                        if (hasDevCenterAccess && onEnterDevCenter) {
                            return (
                                <button key="dev-only" onClick={onEnterDevCenter} className="p-2 text-[#999] hover:text-blue-500 transition-colors" title="Dev Command Center">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="3" width="20" height="14" rx="2" />
                                        <line x1="8" y1="21" x2="16" y2="21" />
                                        <line x1="12" y1="17" x2="12" y2="21" />
                                    </svg>
                                </button>
                            );
                        }
                        return null;
                    })()}

                    <button
                        onClick={onOpenAccountSettings}
                        className="p-2 text-[#999] hover:text-[#FFC107] transition-colors"
                        title="Account Settings"
                    >
                        <Settings size={18} />
                    </button>
                    {onSwitchToAssistant && (
                        <button
                            onClick={onSwitchToAssistant}
                            className="text-sm text-[#999] hover:text-[#FFC107] transition-colors hidden md:flex items-center gap-1"
                        >
                            <Monitor size={14} /> Assistant
                        </button>
                    )}
                    <button
                        onClick={onShowLogoutConfirm}
                        className="p-2 text-[#999] hover:text-red-500 transition-colors"
                        title="Sign Out"
                    >
                        <LogOut size={18} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TeacherWorkspaceHeader;
